import {
  getInsightDetail,
  insightDetailRoute,
  insightListFallbackHref,
  insightContactHref,
  insightSignupHref,
} from "../../../config/insightDetails.js";
import { insightsListHref } from "../../../config/insightsContent.js";
import { buildAnimatedTitle } from "../../HeroTitleAnimation.js";

function escapeHtml(value) {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/"/g, "&quot;");
}

/* Articles only carry a plain title; light the last two words the way the
   other heroes do so the heading reads the same. */
function titleSegments(title) {
  const words = String(title).trim().split(/\s+/);
  if (words.length < 3) return [{ text: title, highlight: "primary" }];
  const head = words.slice(0, -2).join(" ") + " ";
  const [primary, secondary] = words.slice(-2);
  return [
    { text: head, highlight: null },
    { text: primary, highlight: "primary" },
    { text: " ", highlight: null },
    { text: secondary, highlight: "secondary" },
  ];
}

export function createInsightDetailHero(detail) {
  const section = document.createElement("section");
  section.id = "article";
  section.className = "section insight-detail-hero";
  section.style.setProperty("--insight-accent", detail.accent || "#6c8cff");

  section.innerHTML = `
    <div class="insight-detail-hero__wash" aria-hidden="true"></div>
    <div class="section__inner insight-detail-hero__inner">
      <nav class="insight-detail-hero__crumb reveal" aria-label="Breadcrumb">
        <a href="${insightListFallbackHref()}">Insights</a>
        <span aria-hidden="true">/</span>
        <a href="${insightsListHref({ service: detail.serviceId })}">${escapeHtml(detail.serviceLabel)}</a>
        <span aria-hidden="true">/</span>
        <span>${escapeHtml(detail.category)}</span>
      </nav>

      <div class="insight-detail-hero__layout">
        <div class="insight-detail-hero__copy">
          <p class="insight-detail-hero__eyebrow reveal">${escapeHtml(detail.eyebrow)}</p>
          <h1 class="insight-detail-hero__title reveal">${buildAnimatedTitle(titleSegments(detail.title))}</h1>
          <p class="insight-detail-hero__lead reveal">${escapeHtml(detail.lead)}</p>
          <div class="insight-detail-hero__meta reveal">
            <span>${escapeHtml(detail.category)}</span>
            <span>${escapeHtml(detail.readTime)} read</span>
            <span>${escapeHtml(detail.serviceLabel)}</span>
          </div>
        </div>

        <aside class="insight-detail-hero__panel reveal" style="--delay: 0.1s" aria-hidden="true">
          <p class="insight-detail-hero__panel-kicker">Signal</p>
          <p class="insight-detail-hero__panel-quote">“${escapeHtml(detail.pullQuote)}”</p>
          <div class="insight-detail-hero__panel-bar"></div>
        </aside>
      </div>
    </div>
  `;

  return section;
}

export function createInsightDetailBody(detail) {
  const section = document.createElement("section");
  section.id = "body";
  section.className = "section insight-detail-body";
  section.style.setProperty("--insight-accent", detail.accent || "#6c8cff");

  const related = (detail.related || [])
    .map((slug) => getInsightDetail(slug))
    .filter(Boolean);

  section.innerHTML = `
    <div class="section__inner insight-detail-body__inner">
      <div class="insight-detail-takeaways reveal">
        <p class="insight-detail-kicker">Takeaways</p>
        <ol>
          ${detail.takeaways.map((item) => `<li>${escapeHtml(item)}</li>`).join("")}
        </ol>
      </div>

      <article class="insight-detail-article">
        ${detail.sections
          .map(
            (block, index) => `
          <section class="insight-detail-block reveal" style="--delay: ${(index * 0.06).toFixed(2)}s">
            <h2>${escapeHtml(block.heading)}</h2>
            <p>${escapeHtml(block.body)}</p>
          </section>
        `
          )
          .join("")}
      </article>

      ${
        related.length
          ? `
        <div class="insight-detail-related">
          <div class="insight-detail-related__head reveal">
            <p class="insight-detail-kicker">Keep reading</p>
            <h2>Related insights</h2>
          </div>
          <div class="insight-detail-related__grid">
            ${related
              .map(
                (item) => `
              <a
                class="insight-detail-related__card reveal"
                href="${insightDetailRoute(item.slug)}"
                style="--insight-accent: ${item.accent || "#6c8cff"}"
              >
                <span>${escapeHtml(item.category)} · ${escapeHtml(item.readTime)}</span>
                <h3>${escapeHtml(item.title)}</h3>
                <p>${escapeHtml(item.excerpt)}</p>
              </a>
            `
              )
              .join("")}
          </div>
        </div>
      `
          : ""
      }

      <div class="insight-detail-cta reveal" id="next">
        <div>
          <p class="insight-detail-kicker">Next</p>
          <h2>Want this applied to your roadmap?</h2>
          <p>Dummy article for now — register or talk with Taldel to turn the idea into a delivery plan.</p>
        </div>
        <div class="insight-detail-cta__actions">
          <a class="btn btn--primary" href="${insightSignupHref()}">Register</a>
          <a class="btn btn--primary" href="${insightContactHref()}">Talk to us</a>
        </div>
      </div>
    </div>
  `;

  return section;
}
